/**
 * [Phase 2c-D §6.2] CopySettingsModal - wizard-level entry point for returning
 * commissioners. Lists prior leagues the user commissioned, copies their
 * settings into the wizard form on click.
 *
 * SCOPE (terminology lock): This is a SETTINGS TEMPLATE feature. It copies
 *   roster, scoring, draft format, timers, num_teams, budget, nomination
 *   order mode - and EXCLUDES name, invite emails, keepers, draft date, and
 *   league id (excluded server-side by GET /api/users/me/prior-leagues).
 * It is NOT a season carryover, dynasty, keeper-continuation, or legacy-
 * league feature. Managers do NOT retain rosters across seasons.
 *
 * Five-gotcha inverse helper (§6.2.1):
 *   • Gotcha (a) - roster_size re-derived client-side from settings.roster
 *   • Gotcha (b) - expandPreset(scoring_format, scoring_rules) hydrates rules
 *   • Gotcha (c) - parseLeagueType(league_type) yields {top, draftFormat, draftMode, seasonMode}
 *   • Gotcha (d) - undefined league_type falls back to LIVE_DRAFT_LIVE_SEASON
 *   • Gotcha (e) - UI-only keys (top, draftFormat, draftMode, seasonMode) come
 *                  exclusively from parseLeagueType, never from settings
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Copy, Calendar, Users } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";
import { parseCopiedSettings } from "./parseCopiedSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Re-export so existing import paths (`from "./CopySettingsModal"`) keep working.
export { parseCopiedSettings };

export default function CopySettingsModal({ isOpen, onClose, onSelect, isDark = true }) {
  const [leagues, setLeagues] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setLeagues(null);
    setError(null);
    axios
      .get(`${API}/users/me/prior-leagues`, { headers: authHeaders() })
      .then((r) => setLeagues(r.data || []))
      .catch((e) => {
        setError(e?.response?.data?.detail || "Could not load prior leagues.");
        setLeagues([]);
      });
  }, [isOpen]);

  if (!isOpen) return null;

  const bg = isDark ? "#111111" : "#F0F0F0";
  const card = isDark ? "#1C1C1C" : "#FFFFFF";
  const border = isDark ? "#252525" : "#D8D8D8";
  const text = isDark ? "#F5F5F5" : "#0A0A0A";
  const muted = isDark ? "#A0A0A0" : "#404040";

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      data-testid="copy-settings-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border overflow-hidden flex flex-col max-h-[80vh]"
        style={{ background: card, borderColor: border }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: border }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Copy size={14} className="text-[#FF6B00]" />
              <span className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00]">
                Copy League Settings
              </span>
            </div>
            <h3 className="font-display font-black text-xl uppercase" style={{ color: text }}>
              Pick a prior league
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="copy-settings-close-btn"
            className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.12] transition-all"
          >
            <X size={16} style={{ color: text }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3" style={{ background: bg }}>
          {leagues === null && (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {error && (
            <p className="text-sm text-[#FF4444] py-6 text-center" data-testid="copy-settings-error">{error}</p>
          )}
          {leagues && leagues.length === 0 && !error && (
            <p className="text-sm py-10 text-center" style={{ color: muted }} data-testid="copy-settings-empty">
              You haven't commissioned any prior leagues yet. Start fresh below.
            </p>
          )}
          {leagues && leagues.map((lg) => (
            <button
              key={lg.id}
              type="button"
              onClick={() => onSelect(lg)}
              data-testid={`copy-settings-row-${lg.id}`}
              className="w-full text-left mb-2 rounded-xl border p-3 transition-all hover:border-[#FF6B00] hover:bg-[#FF6B00]/5"
              style={{ background: card, borderColor: border }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-display font-black text-base truncate" style={{ color: text }}>
                    {lg.name}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px]" style={{ color: muted }}>
                    <span className="inline-flex items-center gap-1">
                      <Users size={10} />
                      {lg.settings?.num_teams || "-"} teams
                    </span>
                    <span className="font-display font-bold uppercase tracking-wider text-[#FF6B00]">
                      {lg.draft_format || "auction"}
                    </span>
                    {lg.created_at && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={10} />
                        {new Date(lg.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <span className="font-display font-black text-[10px] uppercase tracking-widest text-[#FF6B00] shrink-0">
                  Copy →
                </span>
              </div>
            </button>
          ))}
        </div>

        <div className="px-5 py-3 border-t text-[11px]" style={{ borderColor: border, color: muted }}>
          We'll copy: roster, scoring, draft format, timers, num teams. Excluded: name, invites, keepers, draft date.
        </div>
      </div>
    </div>
  );
}
